import { useEffect, useRef, useState } from 'react';
import { useT } from '@/i18n/runtime';
import type { CaptureCameraApi } from '@/media/useCaptureCamera';
import { Button } from './Button';

/** A4: one stall is said once — a camera that flaps between stalled and live inside this window is not said again. */
export const STALL_ANNOUNCE_GAP_MS = 30_000;

/**
 * US-03482 iPhone black-video RCA, G3: the camera is "live" but sends no picture, even after one automatic restart.
 * Said in words, with the restart under the customer's thumb; the shutter stays disabled (the phase is not `live`)
 * until frames arrive. Rendered last in the viewfinder's bottom stack (round 3: in the clear middle, held sideways, the
 * bottom stack lay over its button), so it is in the stack's flow and nothing covers it.
 *
 * Round 3 (A4): the same card for a camera that freezes later in the step. A screen reader hears it POLITELY, from a
 * live region that stays mounted (as the location gate's does), and once per stall: a re-render, or a camera flapping
 * between stalled and live within STALL_ANNOUNCE_GAP_MS, is not read out again.
 */
export function CameraStalled({ cam }: { cam: Pick<CaptureCameraApi, 'phase' | 'restart'> }): JSX.Element {
  const { t } = useT();
  const stalled = cam.phase === 'stalled';
  const [spoken, setSpoken] = useState('');
  const saidAt = useRef<number | null>(null);
  useEffect(() => {
    if (!stalled) {
      setSpoken('');
      return;
    }
    const now = Date.now();
    if (saidAt.current !== null && now - saidAt.current < STALL_ANNOUNCE_GAP_MS) return;
    saidAt.current = now;
    setSpoken(t('sv.cam.stalled.title'));
  }, [stalled, t]);
  return (
    <>
      <p className="sv-sr-only" aria-live="polite" data-testid="camera-stalled-live">{spoken}</p>
      {stalled ? (
        <div className="vp-hint sv-cam-stalled" data-testid="camera-stalled">
          <div>{t('sv.cam.stalled.title')}</div>
          <div style={{ marginTop: 8 }}>
            <Button onClick={cam.restart} data-testid="camera-restart">{t('sv.cam.stalled.restart')}</Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
